<template>
  <span class="inline-block delay-900" :class="{ 'has-tooltip': showTooltip }">
    <img
      v-if="computed_image"
      :class="[
        {
          'border-2 ring-2 ring-red-500 opacity-80': !isCompany && !email && noValidEmailEffect,
          'ring-2 ring-blue-500': isAgent
        }
      ]"
      class="inline-block rounded-full h-9 w-9 object-cover"
      :src="computed_image"
      :style="
        !isNaN(radius)
          ? {
            height: radius + 'px',
            width: radius + 'px',
            minHeight: radius + 'px',
            minWidth: radius + 'px'
          }
          : { height: radius, minHeight: radius }
      "
      :onerror="onerror_placeholder_img"
      alt="">

    <div
      v-else
      ref="avatarRef"
      :class="[
        {
          'group-hover/row:bg-gray-200 group-hover/row:text-blue': hoverEffect,
          'rounded-md shadow-sm': isCompany,
          'rounded-full': !isCompany,
          'border-2 ring-2 ring-red-500': !isCompany && !email && noValidEmailEffect,
          'aspect-square': isNaN(radius),
          '!bg-purple-700': owner,
          'ring-2 ring-blue-500': isAgent
        }
      ]"
      :style="
        !isNaN(radius)
          ? {
            height: radius + 'px',
            width: radius + 'px',
            minHeight: radius + 'px',
            minWidth: radius + 'px'
          }
          : { height: radius, minHeight: radius }
      "
      class="inline-block has-tooltip bg-light-brand-blue dark:bg-dark-brand-blue">
      <div
        :style="{ fontSize: `${text_size}px` }"
        :class="{ 'group-hover/row:text-blue': hoverEffect }"
        class="flex items-center justify-center w-full h-full font-semibold text-white uppercase">
        <span>{{ first_name_initial }}</span><span>{{ last_name_initial }}</span>
      </div>
      <span
        v-if="showTooltip"
        :class="[
          {
            'bg-red-500 text-white': !isCompany && !email && noValidEmailEffect
          }
        ]"
        class="inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip whitespace-nowrap bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
        {{ object_tooltip }}
      </span>
    </div>
  </span>
</template>

<script>
import { Record } from "@/models/record"
import { utils } from "@/store/helpers"
export default {
  name: "Avatar",
  props: {
    owner: null,
    h: {
      type: Number,
      required: false
    },
    w: {
      type: Number,
      required: false
    },
    lastName: {
      type: String,
      default: null,
      required: false
    },
    firstName: {
      type: String,
      default: null,
      required: false
    },
    name: {
      type: String,
      default: null,
      required: false
    },
    email: {
      type: String,
      default: null,
      required: false
    },
    image: {
      type: String,
      default: null,
      required: false
    },
    tooltip: {
      type: String,
      default: null,
      required: false
    },
    hoverEffect: {
      type: Boolean,
      default: false
    },
    isCompany: {
      type: Boolean,
      default: false
    },
    isAgent: {
      type: Boolean,
      default: false
    },
    noValidEmailEffect: {
      type: Boolean,
      default: false
    },
    showTooltip: {
      type: Boolean,
      default: true
    },
    radius: {
      type: [Number, String],
      default: 32
    },
    user: {
      type: [Object, String],
      default: null
    },
    avatar: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      user_object: null,
      text_size: 12
    }
  },

  computed: {
    computed_name() {
      return this.user_object?.full_name || this.name
    },
    computed_email() {
      return this.user_object?.email || this.email
    },
    computed_image() {
      return this.user_object?.avatar || this.image || this.avatar
    },
    computed_first_name() {
      return this.user_object?.first_name || this.firstName
    },
    computed_last_name() {
      return this.user_object?.last_name || this.lastName
    },

    object_tooltip() {
      if (this.showTooltip) {
        if (!this.isCompany) {
          if (!this.computed_email && this.noValidEmailEffect) {
            return `${this.full_name} ` + this.$t("has no valid email address")
          } else {
            return `${this.full_name} <${this.computed_email}>`
          }
        }
        return this.tooltip
      } else {
        return null
      }
    },
    full_name() {
      return [this.computed_first_name, this.computed_last_name].filter(Boolean).join(" ")
    },
    onerror_placeholder_img() {
      return `this.onerror=null; this.src=${this.isCompany ? "window.default_company" : "window.default_avatar"};`
    },
    first_name_initial() {
      if (utils.isNotEmpty(this.computed_first_name)) {
        if (String(this.computed_first_name).length > 0) {
          return String(this.computed_first_name).substring(0, 1)
        }
      }
      // split the name and get the first letter
      if (utils.isNotEmpty(this.computed_name)) {
        if (String(this.computed_name).length > 0) {
          return String(this.computed_name).substring(0, 1)
        }
      }
      return ""
    },
    last_name_initial() {
      if (utils.isNotEmpty(this.computed_last_name)) {
        if (String(this.computed_last_name).length > 0) {
          return String(this.computed_last_name).substring(0, 1)
        }
      }
      // split the name and get the first letter of second word
      if (utils.isNotEmpty(this.computed_name)) {
        if (String(this.computed_name).length > 0) {
          if (String(this.computed_name).split(" ").length > 1) {
            return String(this.computed_name).split(" ")[1].substring(0, 1)
          }
        }
      }

      return ""
    }
  },
  watch: {
    h: {
      deep: true,
      handler() {
        this.text_size = this.$refs.avatarRef?.clientWidth / 3
      }
    },
    w: {
      deep: true,
      handler() {
        this.text_size = this.$refs.avatarRef?.clientWidth / 3
      }
    }
  },
  created() {
    // if the user is just an id get the
    if (utils.isUUID(this.user)) {
      this.user_object = Record.from(this.user)
    } else {
      this.user_object = this.user
    }
  },
  mounted() {
    if (this.$refs.avatarRef?.clientWidth) {
      this.text_size = this.$refs.avatarRef?.clientWidth / 3
    }
  },
  activated() {
    if (this.$refs.avatarRef?.clientWidth) {
      this.text_size = this.$refs.avatarRef?.clientWidth / 3
    }
  }
}
</script>
